import { Story } from "@/components/builder/stories/context";
import { StoriesFilter, StoriesListContext, StoriesListContextType } from "@/components/builder/stories/list/context";
import { DEFAULT_BUILDER_LOCALE } from "@/util/constants";
import { useLocale } from "@/libs/base/components/locale-provider";
import { BuilderIoQueryBuilder } from "@/libs/builder/services/builder-io-query-builder";
import StoriesService from "@/services/stories-service";
import { withChildren } from "@builder.io/react";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";

export interface StoriesContextProviderProps {
  children: ReactNode;
  storiesPerLoad: number;
  startDate?: string;
  endDate?: string;
}

export function StoriesContextProvider(props: StoriesContextProviderProps) {
  const locale = useLocale();
  const service = useMemo(() => new StoriesService(locale), [locale]);

  const [page, setPage] = useState(0);

  const [filter, setFilter] = useState<StoriesFilter>({
    genres: [],
    startDate: props.startDate ? new Date(props.startDate) : undefined,
    endDate: props.endDate ? new Date(props.endDate) : undefined,
    excludeIdsIfNoGenreFilterIsActive: StoriesGroupHelper.findStoryIdsFromReactNode(props.children),
  });

  // TODO: Switch over to using @tanstack/query which handles all of the loading states, infinite scroll, etc.
  const [stories, setStories] = useState(new Set<Story>());
  const [loadingStories, setStoriesLoading] = useState(false);
  const [moreStoriesAvailable, setMoreStoriesAvailable] = useState(false);

  const [genres, setGenres] = useState<string[]>([]);
  const [loadingGenres, setGenresLoading] = useState(false);

  const fetchStories = useCallback(
    async (reload = false) => {
      Promise.resolve(setStoriesLoading(true))
        .then(() =>
          service.getStories({
            page: page + 1,
            storiesPerLoad: props.storiesPerLoad,
            query: buildQuery(filter, locale),
            omit: "data.blocks",
          }),
        )
        .then((data) => {
          setStories((stories) => {
            if (reload) stories.clear();
            for (const story of data) stories.add(story);

            return stories;
          });

          setMoreStoriesAvailable(data.length == props.storiesPerLoad);
        })
        .finally(() => setStoriesLoading(false));
    },
    [service, page, filter, props.storiesPerLoad, setStories, setStoriesLoading, setMoreStoriesAvailable],
  );

  const fetchGenres = useCallback(
    () =>
      Promise.resolve(setGenresLoading(true))
        .then(() => service.getGenres())
        .then((data) => setGenres(data))
        .finally(() => setGenresLoading(false)),
    [service, setGenres, setGenresLoading],
  );

  useEffect(() => {
    // Fetch genres on mount
    fetchGenres();
  }, []);

  useEffect(() => {
    // Reload stories because of a filter change
    fetchStories(true);
  }, [filter]);

  const ctx: StoriesListContextType = useMemo(
    () =>
      ({
        service,
        page,
        storiesPerLoad: props.storiesPerLoad,
        filter,

        genres,
        loadingGenres,
        selectGenres: (genres) => {
          setStories((stories) => {
            stories.clear();
            return stories;
          });
          setFilter((filter) => ({ ...filter, genres }));
        },

        stories,
        loadingStories,
        moreStoriesAvailable,
        loadMoreStories: async () => {
          setPage((page) => page + 1);
          await fetchStories();
        },
      }) satisfies StoriesListContextType,
    [
      service,
      page,
      props.storiesPerLoad,
      filter,

      genres,
      loadingGenres,
      setStories,

      stories,
      loadingStories,
      moreStoriesAvailable,
      setFilter,
      setPage,
      fetchStories,
    ],
  );

  return <StoriesListContext.Provider value={ctx}>{props.children}</StoriesListContext.Provider>;
}

function buildQuery(filter: StoriesFilter, locale: string) {
  const queryBuilder = new BuilderIoQueryBuilder()
    .addDateRange("firstPublished", filter.startDate, filter.endDate)
    .addAllInArray(`data.genres.${locale === "en" ? DEFAULT_BUILDER_LOCALE : locale}`, filter.genres);

  if (filter.genres.length == 0) {
    // We only want to exclude stories if no genre filter is active because in this case we do NOT display story highlights
    queryBuilder.addNotInArray("id", filter.excludeIdsIfNoGenreFilterIsActive);
  }

  // FIXME: Builder.io text search doesn't work yet, so we use client-side filtering which
  //        is limited to only filtering what's already been loaded
  // if (filter.title) queryBuilder.addTextSearch("title", filter.title);

  return queryBuilder.build();
}

export const StoriesGroupWithChildren = withChildren(StoriesContextProvider);

class StoriesGroupHelper {
  private static readonly KEYS = ["props", "block", "blocks", "component", "options", "children", "columns"];
  private static readonly STORY_HIGHLIGHT_COMPONENT_NAME = "StoryHighlight";

  static findStoryIdsFromReactNode(obj: ReactNode): string[] {
    return this.findStoryIds(obj);
  }

  private static findStoryIds(obj: any): string[] {
    if (!obj) {
      return [];
    }

    let ids: string[] = [];

    if (Array.isArray(obj)) {
      for (const item of obj) {
        ids = ids.concat(this.findStoryIds(item));
      }
      return ids;
    }

    if (typeof obj === "object") {
      if (this.isStoryHighlightComponent(obj)) {
        return obj.component.options.story.id;
      }

      for (const key in obj) {
        if (!this.KEYS.includes(key) && !Number(key)) {
          continue;
        }

        if (obj.hasOwnProperty(key)) {
          ids = ids.concat(this.findStoryIds(obj[key]));
        }
      }
    }

    return ids;
  }

  private static isStoryHighlightComponent(obj: any): boolean {
    return (
      obj.component && obj.component.name == this.STORY_HIGHLIGHT_COMPONENT_NAME && obj.component.options?.story?.id
    );
  }
}
